import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import usePermissions from "../hook/usePermissions";
import TablePagination from "./TablePagination";
import AdminUserApi from "../Api/AdminUserApi";
import { toast } from "react-toastify";

const AdminRegistrationListLayer = () => {
    const { hasPermission } = usePermissions();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                limit: rowsPerPage,
                search: debouncedSearchTerm,
            };
            const response = await AdminUserApi.getAdminUsers(params);
            if (response && response.status && response.response) {
                const data = response.response.data;
                setUsers(data || []);
                setTotalRecords(response.response.total || 0);
            } else {
                setUsers([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error("Failed to fetch admin users", error);
            setUsers([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [currentPage, rowsPerPage, debouncedSearchTerm]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(0);
    };

    const confirmDelete = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (userToDelete) {
            const response = await AdminUserApi.deleteAdminUser(userToDelete._id);
            if (response && response.status) {
                fetchUsers();
                setShowDeleteModal(false);
                setUserToDelete(null);
            }
        }
    };

    const handleToggleStatus = async (user) => {
        const response = await AdminUserApi.toggleStatus(user._id);
        if (response && response.status) {
            fetchUsers();
        }
    };

    const totalPages = Math.ceil(totalRecords / rowsPerPage);

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <h6 className="text-primary-600 pb-2 mb-0" style={{ color: '#002B5B' }}>Admin Users List</h6>
                </div>

                <div className="d-flex align-items-center flex-wrap gap-3">
                    <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
                        <input
                            type="text"
                            className="bg-base h-40-px w-auto radius-8 border"
                            name="search"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        <Icon icon="ion:search-outline" className="icon" />
                    </form>
                    {hasPermission("Admin Registration", "add") && (
                        <Link
                            to="/admin-registration/add"
                            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                            style={{ backgroundColor: "#D81B60", borderColor: "#D81B60" }}
                        >
                            <Icon icon="ic:baseline-plus" className="text-xl" />
                            Add User
                        </Link>
                    )}
                </div>
            </div>
            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>S.No</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Name</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Email</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Phone Number</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Role</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Status</th>
                                <th scope="col" style={{ color: "black", fontWeight: '600' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="text-center py-24">Loading...</td></tr>
                            ) : users.length > 0 ? (
                                users.map((user, index) => (
                                    <tr key={user._id}>
                                        <td>{currentPage * rowsPerPage + index + 1}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="w-40-px h-40-px bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center rounded-circle">
                                                    <span className="text-lg fw-semibold">{user.name?.charAt(0)?.toUpperCase() || "A"}</span>
                                                </div>
                                                <span className="text-md mb-0 fw-normal text-secondary-light">{user.name}</span>
                                            </div>
                                        </td>
                                        <td>{user.email}</td>
                                        <td>{user.phoneNumber}</td>
                                        <td>
                                            <span className="badge bg-info-focus text-info-600 px-12 py-4 radius-4">
                                                {user.roleName || user.roleId?.name || "-"}
                                            </span>
                                        </td>
                                        <td>
                                            <span 
                                                onClick={() => handleToggleStatus(user)}
                                                className={`badge px-24 py-4 radius-4 fw-medium text-sm cursor-pointer ${user.isActive === 1 ? "bg-success-focus text-success-main" : "bg-danger-focus text-danger-main"}`}
                                            >
                                                {user.isActive === 1 ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                {hasPermission("Admin Registration", "edit") && (
                                                    <Link to={`/admin-registration/edit/${user._id}`} className="bg-success-focus text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle">
                                                        <Icon icon="lucide:edit" className="menu-icon" />
                                                    </Link>
                                                )}
                                                {hasPermission("Admin Registration", "delete") && (
                                                    <button onClick={() => confirmDelete(user)} className="bg-danger-focus text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0">
                                                        <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="7" className="text-center py-24">No users found</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {!loading && (
                    <TablePagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(0); }}
                        totalRecords={totalRecords}
                    />
                )}
            </div>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Body className="text-center p-5">
                    <div className="d-flex justify-content-center mb-3">
                        <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
                            <Icon icon="fluent:delete-24-regular" className="text-danger-600 text-xxl" />
                        </div>
                    </div>
                    <h5 className="mb-3">Are you sure?</h5>
                    <p className="text-secondary-light mb-4">Do you want to delete user "{userToDelete?.name}"?</p>
                    <div className="d-flex justify-content-center gap-3">
                        <Button variant="outline-secondary" className="px-32" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                        <Button variant="danger" className="px-32" style={{ backgroundColor: "#D81B60", borderColor: "#D81B60" }} onClick={handleDelete}>Delete</Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AdminRegistrationListLayer;
